import apiFetch from './client';

export type NotificationType =
  | 'SPOT_USAGE_REQUEST'
  | 'SPOT_USAGE_CANCELLED'
  | 'SPOT_USAGE_APPROVED'
  | 'SPOT_USAGE_REJECTED'
  | 'SPOT_USAGE_EXPIRED'
  | 'SPOT_USAGE_RELEASED'
  | 'SPOT_USAGE_RECLAIMED'
  | 'SPOT_MISUSE_REPORT'
  | 'SPOT_MISUSE_RESOLVED'
  | 'BULK_SPOT_REQUEST'
  | 'BULK_SPOT_REQUEST_EXPIRED'
  | 'SPOT_AVAILABLE'
  | 'AVAILABILITY_WINDOW_UPDATED';

export interface AppNotification {
  publicId: string;
  type: NotificationType | string;
  title: string;
  message: string;
  read: boolean;
  metadata?: string | null;
  createdAt: string;
}

interface UnreadCountResponse {
  count: number;
}

interface DeleteReadNotificationsResponse {
  deletedCount: number;
}

export async function getNotifications(): Promise<AppNotification[]> {
  const data = await apiFetch<AppNotification[]>('/notifications');
  return data ?? [];
}

export async function getUnreadCount(): Promise<number> {
  const response = await apiFetch<UnreadCountResponse>('/notifications/unread/count');
  return response.count ?? 0;
}

export async function markNotificationAsRead(notificationId: string): Promise<AppNotification> {
  return apiFetch<AppNotification>(`/notifications/${notificationId}/read`, {
    method: 'PUT',
  });
}

export async function markAllNotificationsAsRead(): Promise<void> {
  await apiFetch<void>('/notifications/read-all', { method: 'PUT' });
}

export async function deleteReadNotifications(): Promise<number> {
  const response = await apiFetch<DeleteReadNotificationsResponse>('/notifications/read', {
    method: 'DELETE',
  });
  return response.deletedCount ?? 0;
}

export async function registerPushToken(token: string, deviceLabel?: string): Promise<void> {
  await apiFetch<void>('/notifications/push-token', {
    method: 'PUT',
    body: JSON.stringify({ token, deviceLabel }),
  });
}

export interface NotificationPreferencesResponse {
  configured: boolean;
  pushEnabled: boolean;
  hasPushToken: boolean;
  spotUsageRequestEnabled: boolean;
  spotUsageCancelledEnabled: boolean;
  spotUsageApprovedEnabled: boolean;
  spotUsageRejectedEnabled: boolean;
  spotUsageExpiredEnabled: boolean;
  bulkSpotRequestEnabled: boolean;
  bulkSpotRequestExpiredEnabled: boolean;
  spotAvailableEnabled: boolean;
}

export type NotificationPreferencesRequest = Omit<
  NotificationPreferencesResponse,
  'configured' | 'hasPushToken'
>;

export async function getNotificationPreferences(): Promise<NotificationPreferencesResponse> {
  return apiFetch<NotificationPreferencesResponse>('/notifications/preferences');
}

export async function updateNotificationPreferences(
  prefs: NotificationPreferencesRequest,
): Promise<void> {
  await apiFetch<void>('/notifications/preferences', {
    method: 'PUT',
    body: JSON.stringify(prefs),
  });
}

export async function setPushEnabledPreference(pushEnabled: boolean): Promise<void> {
  const current = await getNotificationPreferences();
  await updateNotificationPreferences({
    pushEnabled,
    spotUsageRequestEnabled: current.spotUsageRequestEnabled,
    spotUsageCancelledEnabled: current.spotUsageCancelledEnabled,
    spotUsageApprovedEnabled: current.spotUsageApprovedEnabled,
    spotUsageRejectedEnabled: current.spotUsageRejectedEnabled,
    spotUsageExpiredEnabled: current.spotUsageExpiredEnabled,
    bulkSpotRequestEnabled: current.bulkSpotRequestEnabled,
    bulkSpotRequestExpiredEnabled: current.bulkSpotRequestExpiredEnabled,
    spotAvailableEnabled: current.spotAvailableEnabled,
  });
}

export const notificationsKeys = {
  all: ['notifications'] as const,
  list: () => [...notificationsKeys.all, 'list'] as const,
  unreadCount: () => [...notificationsKeys.all, 'unread-count'] as const,
  preferences: () => [...notificationsKeys.all, 'preferences'] as const,
};
